import { Facebook, Twitter, Instagram, Mail, Phone, MapPin, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/hooks/useLanguage";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { NewsletterSubscribe } from "@/components/NewsletterSubscribe";

interface FooterSection {
  id: string;
  section_key: string;
  title: string;
  content: any;
  sort_order: number;
  active: boolean;
}

const Footer = () => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const [footerSections, setFooterSections] = useState<FooterSection[]>([]);
  const [categories, setCategories] = useState<{slug: string, count: number}[]>([]);
  
  // Convertir les slugs en libellés lisibles
  const formatCategoryLabel = (slug: string): string => {
    const categoryLabels: Record<string, string> = {
      'mode-beaute': 'Mode & Beauté',
      'maison-jardin': 'Maison & Jardin',
      'electronique': 'Électronique',
      'sante-bien-etre': 'Santé & Bien-être',
      'livres-culture': 'Livres & Culture',
      'auto-moto': 'Auto & Moto',
      'sport-loisirs': 'Sport & Loisirs',
      'enfants-bebes': 'Enfants & Bébés',
      'jeux-jouets': 'Jeux & Jouets',
      'high-tech': 'High-Tech',
      'alimentation': 'Alimentation',
      'services': 'Services',
    };
    
    return categoryLabels[slug] || slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  
  useEffect(() => {
    fetchFooterSections();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // First try to get categories from the categories table
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('slug, name_translations')
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (!categoriesError && categoriesData && categoriesData.length > 0) {
        // Use categories from database
        const formattedCategories = categoriesData.map(cat => ({
          slug: cat.slug,
          count: 0, // We'll skip count display
          label: (cat.name_translations as Record<string, string>)?.fr || formatCategoryLabel(cat.slug)
        }));
        setCategories(formattedCategories as any);
        return;
      }

      // Fallback: count products by category
      const { data: products, error } = await supabase
        .from('products')
        .select('category')
        .eq('active', true);

      if (error) throw error;

      // Count products by category
      const categoryCount = (products || []).reduce((acc: any, product: any) => {
        if (product.category) {
          acc[product.category] = (acc[product.category] || 0) + 1;
        }
        return acc;
      }, {});

      // Convert to array and sort by count
      const sortedCategories = Object.entries(categoryCount)
        .map(([slug, count]) => ({ slug, count: count as number }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6); // Top 6 categories

      // If no products, show default categories
      if (sortedCategories.length === 0) {
        setCategories([
          { slug: 'mode-beaute', count: 0 },
          { slug: 'electronique', count: 0 },
          { slug: 'maison-jardin', count: 0 },
          { slug: 'enfants-bebes', count: 0 },
          { slug: 'sante-bien-etre', count: 0 },
          { slug: 'sport-loisirs', count: 0 }
        ]);
      } else {
        setCategories(sortedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Set default categories on error
      setCategories([
        { slug: 'mode-beaute', count: 0 },
        { slug: 'electronique', count: 0 },
        { slug: 'maison-jardin', count: 0 },
        { slug: 'enfants-bebes', count: 0 },
        { slug: 'sante-bien-etre', count: 0 },
        { slug: 'sport-loisirs', count: 0 }
      ]);
    }
  };

  const fetchFooterSections = async () => {
    try {
      const { data, error } = await supabase
        .from('footer_sections')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      const sections = (data || []).map(section => ({
        ...section,
        content: typeof section.content === 'string' ? JSON.parse(section.content) : section.content
      }));
      
      setFooterSections(sections);
    } catch (error) {
      console.error('Error fetching footer sections:', error);
    }
  };

  // Get specific sections by key
  const quickLinksSection = footerSections.find(s => s.section_key === 'quick_links');
  const quickLinks = Array.isArray(quickLinksSection?.content) ? quickLinksSection.content : [];

  // Map French titles from database to translation keys
  const getTranslatedTitle = (frenchTitle: string): string => {
    const titleMap: Record<string, string> = {
      'À propos': t('footer.about'),
      'Comment ça marche': t('footer.how_it_works'),
      'Devenir vendeur': t('footer.become_vendor'),
      'Points relais': t('footer.relay_points'),
      'Support': t('footer.support'),
      'Conditions d\'utilisation': t('footer.terms'),
      'Politique de confidentialité': t('footer.privacy'),
    };
    return titleMap[frenchTitle] || frenchTitle;
  };

  // Get social links from footer_sections (CMS) — maps icon name to Lucide component
  const socialLinksSection = footerSections.find(s => s.section_key === 'social_links');
  const cmsocialLinks: { title: string; url: string; icon: string }[] = Array.isArray(socialLinksSection?.content) ? socialLinksSection.content : [];

  const getSocialIcon = (icon: string) => {
    switch (icon?.toLowerCase()) {
      case 'facebook': return <Facebook className="h-5 w-5" />;
      case 'twitter': case 'x': return <Twitter className="h-5 w-5" />;
      case 'instagram': return <Instagram className="h-5 w-5" />;
      default: return null;
    }
  };

  // Use CMS social links if available, otherwise fall back to site_settings
  const useCMSSocial = cmsocialLinks.length > 0;

  return (
    <footer className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1: Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt={settings.site_name} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary">
                    <Package className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h1 className="text-2xl font-bold">{settings.site_name}</h1>
                </>
              )}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              {useCMSSocial ? (
                cmsocialLinks.filter(l => l.url).map((link, i) => (
                  <Button
                    key={i}
                    variant="ghost"
                    size="sm"
                    className="p-2 hover:bg-white/10"
                    onClick={() => window.open(link.url, '_blank')}
                    title={link.title || link.icon}
                  >
                    {getSocialIcon(link.icon) || <span className="text-xs">{link.title || link.icon}</span>}
                  </Button>
                ))
              ) : (
                <>
                  {settings.footer_facebook && (
                    <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10" onClick={() => window.open(settings.footer_facebook, '_blank')}>
                      <Facebook className="h-5 w-5" />
                    </Button>
                  )}
                  {settings.footer_twitter && (
                    <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10" onClick={() => window.open(settings.footer_twitter, '_blank')}>
                      <Twitter className="h-5 w-5" />
                    </Button>
                  )}
                  {settings.footer_instagram && (
                    <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10" onClick={() => window.open(settings.footer_instagram, '_blank')}>
                      <Instagram className="h-5 w-5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('footer.quick_links')}</h3>
            <ul className="space-y-2">
            {quickLinks.map((item: any, index: number) => {
              const title = item.title || item.label || item.text;
              const translatedTitle = getTranslatedTitle(title);
              
              return (
                <li key={index}>
                  {item.url?.startsWith('/') ? (
                    <Link 
                      to={item.url}
                      className="text-muted-foreground hover:text-secondary transition-colors"
                    >
                      {translatedTitle}
                    </Link>
                  ) : (
                    <a 
                      href={item.url || '#'} 
                      className="text-muted-foreground hover:text-secondary transition-colors"
                      target={item.url?.startsWith('http') ? '_blank' : undefined}
                      rel={item.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {translatedTitle}
                    </a>
                  )}
                </li>
              );
            })}
            </ul>
          </div>

          {/* Column 3: Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('footer.categories')}</h3>
            <ul className="space-y-2">
              {categories.length > 0 ? (
                categories.map(({ slug, count }) => (
                  <li key={slug}>
                    <Link 
                      to={`/category/${encodeURIComponent(slug)}`} 
                      className="text-muted-foreground hover:text-secondary transition-colors flex justify-between"
                    >
                      <span>{formatCategoryLabel(slug)}</span>
                      {count > 0 && <span className="text-xs opacity-60">({count})</span>}
                    </Link>
                  </li>
                ))
              ) : (
                <>
                  <li>
                    <Link to="/category/electronique" className="text-muted-foreground hover:text-secondary transition-colors">
                      {t('categories.electronics')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/category/mode-beaute" className="text-muted-foreground hover:text-secondary transition-colors">
                      {t('categories.fashion')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/category/maison-jardin" className="text-muted-foreground hover:text-secondary transition-colors">
                      {t('categories.home')}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Column 4: Contact & Newsletter */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('footer.contact')}</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground text-sm">{settings.footer_address}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground text-sm">{settings.support_phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground text-sm">{settings.contact_email}</span>
              </div>
            </div>
            
            <div className="pt-2">
              <NewsletterSubscribe />
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8 text-center space-y-4">
          <div className="flex justify-center space-x-6">
            <Link to="/terms" className="text-muted-foreground hover:text-secondary transition-colors text-sm">
              {t('footer.terms')}
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-secondary transition-colors text-sm">
              {t('footer.privacy')}
            </Link>
            <Link to="/legal" className="text-muted-foreground hover:text-secondary transition-colors text-sm">
              {t('footer.legal')}
            </Link>
          </div>
          <p className="text-muted-foreground">
            {t('footer.copyright')}
          </p>
          <p className="text-xs text-muted-foreground/40 mt-1">
            v{import.meta.env.VITE_APP_VERSION}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;